import React from 'react'
import { Box, TextField, Button, Typography, useTheme as useMuiTheme } from '@mui/material'
import { useTheme } from '../../context/ThemeContext'
import { Add, Remove } from '@mui/icons-material'

const TextEditorContacts = ({ contacts, onContactsChange, sectionType }) => {
  const { isDarkMode } = useTheme()
  const theme = useMuiTheme()

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const validatePhone = (phone) => {
    const phoneRegex = /^\+?[\d\s-()]{7,}$/
    return phoneRegex.test(phone)
  }

  const validateWebsite = (website) => {
    if (!website) return true
    const websiteRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/
    return websiteRegex.test(website)
  }

  const validateSocials = (social) => {
    if (!social) return true
    const socialRegex = /^(https?:\/\/)?(www\.)?(linkedin\.com|twitter\.com|facebook\.com|instagram\.com)\/.+$/
    return socialRegex.test(social)
  }

  const handleContactChange = (field, value, index = 0) => {
    onContactsChange({
      ...contacts,
      [field]: Array.isArray(contacts[field]) ? contacts[field].map((item, i) => (i === index ? { ...item, value } : item)) : value,
    })
  }

  const addContactField = (field) => {
    onContactsChange({
      ...contacts,
      [field]: [...contacts[field], { id: Date.now(), value: '' }],
    })
  }

  const removeContactField = (field, index) => {
    onContactsChange({
      ...contacts,
      [field]: contacts[field].filter((_, i) => i !== index),
    })
  }

  // Common textfield styling to maintain consistency
  const textFieldStyle = {
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': {
        borderColor: theme.palette.mode === 'dark' ? 'secondary.dark' : 'secondary.light',
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.mode === 'dark' ? 'secondary.dark' : 'secondary.light',
      },
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.mode === 'dark' ? 'secondary.dark' : 'secondary.light',
    },
    '& .MuiInputBase-root': {
      fontSize: { xs: '0.9rem', sm: '1rem' },
    },
  }

  // Common button styling
  const buttonStyle = {
    minWidth: '40px',
    height: '56px',
    borderColor: theme.palette.mode === 'dark' ? 'secondary.dark' : 'secondary.light',
    color: theme.palette.mode === 'dark' ? 'secondary.dark' : 'secondary.light',
    '&:hover': {
      borderColor: theme.palette.mode === 'dark' ? 'secondary.dark' : 'secondary.light',
      backgroundColor: 'rgba(111, 0, 255, 0.04)',
    },
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography
        variant='h6'
        sx={{
          color: theme.palette.mode === 'dark' ? 'secondary.dark' : 'secondary.light',
          mb: 1,
          fontWeight: 500,
        }}
      >
        {sectionType} Information
      </Typography>

      <TextField
        label='Name'
        value={contacts.name}
        onChange={(e) => handleContactChange('name', e.target.value)}
        fullWidth
        required
        sx={textFieldStyle}
      />

      <TextField
        label='Location'
        value={contacts.location}
        onChange={(e) => handleContactChange('location', e.target.value)}
        fullWidth
        sx={textFieldStyle}
      />

      {contacts.email.map((email, index) => (
        <Box key={email.id} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            label='Email'
            value={email.value}
            onChange={(e) => handleContactChange('email', e.target.value, index)}
            fullWidth
            required
            error={email.value && !validateEmail(email.value)}
            helperText={email.value && !validateEmail(email.value) ? 'Please enter a valid email address' : ''}
            sx={textFieldStyle}
          />
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {index === contacts.email.length - 1 && (
              <Button onClick={() => addContactField('email')} variant='outlined' sx={buttonStyle}>
                <Add fontSize='small' />
              </Button>
            )}
            {contacts.email.length > 1 && (
              <Button onClick={() => removeContactField('email', index)} variant='outlined' color='error' sx={{ minWidth: '40px', height: '56px' }}>
                <Remove fontSize='small' />
              </Button>
            )}
          </Box>
        </Box>
      ))}

      {contacts.phone.map((phone, index) => (
        <Box key={phone.id} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            label='Phone'
            value={phone.value}
            onChange={(e) => handleContactChange('phone', e.target.value, index)}
            fullWidth
            error={phone.value && !validatePhone(phone.value)}
            helperText={phone.value && !validatePhone(phone.value) ? 'Please enter a valid phone number' : ''}
            sx={textFieldStyle}
          />
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {index === contacts.phone.length - 1 && (
              <Button onClick={() => addContactField('phone')} variant='outlined' sx={buttonStyle}>
                <Add fontSize='small' />
              </Button>
            )}
            {contacts.phone.length > 1 && (
              <Button onClick={() => removeContactField('phone', index)} variant='outlined' color='error' sx={{ minWidth: '40px', height: '56px' }}>
                <Remove fontSize='small' />
              </Button>
            )}
          </Box>
        </Box>
      ))}

      {contacts.socials.map((social, index) => (
        <Box key={social.id} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            label='Socials'
            value={social.value}
            onChange={(e) => handleContactChange('socials', e.target.value, index)}
            fullWidth
            error={social.value && !validateSocials(social.value)}
            helperText={social.value && !validateSocials(social.value) ? 'Please enter a valid social media URL' : ''}
            sx={textFieldStyle}
          />
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {index === contacts.socials.length - 1 && (
              <Button onClick={() => addContactField('socials')} variant='outlined' sx={buttonStyle}>
                <Add fontSize='small' />
              </Button>
            )}
            {contacts.socials.length > 1 && (
              <Button onClick={() => removeContactField('socials', index)} variant='outlined' color='error' sx={{ minWidth: '40px', height: '56px' }}>
                <Remove fontSize='small' />
              </Button>
            )}
          </Box>
        </Box>
      ))}

      {contacts.website.map((website, index) => (
        <Box key={website.id} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            label='Personal Website'
            value={website.value}
            onChange={(e) => handleContactChange('website', e.target.value, index)}
            fullWidth
            error={website.value && !validateWebsite(website.value)}
            helperText={website.value && !validateWebsite(website.value) ? 'Please enter a valid website URL' : ''}
            sx={textFieldStyle}
          />
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {index === contacts.website.length - 1 && (
              <Button onClick={() => addContactField('website')} variant='outlined' sx={buttonStyle}>
                <Add fontSize='small' />
              </Button>
            )}
            {contacts.website.length > 1 && (
              <Button onClick={() => removeContactField('website', index)} variant='outlined' color='error' sx={{ minWidth: '40px', height: '56px' }}>
                <Remove fontSize='small' />
              </Button>
            )}
          </Box>
        </Box>
      ))}

      <Typography
        variant='caption'
        sx={{
          mt: 1,
          color: theme.palette.mode === 'dark' ? 'text.secondary' : 'text.secondary',
          fontStyle: 'italic',
        }}
      >
        Contact information will be saved with your profile
      </Typography>
    </Box>
  )
}

export default TextEditorContacts
